import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { KIT_ASSET_WORKING_STATUSES, SerializedKitAsset } from '../../../../redux/api/fymAPI.types';
import type { ReelStatus, SerializedReel } from '../types';

/**
 * The links "See the work" and "See the progress" (owner, 2026-10-11): the Studio cards, the Studio
 * banner after "Make it" and the working banner of the release page open the Work page while a
 * file is queued, made now or an open failure. The link is `promoteWork?catalogItemUuid=<song>&
 * state=working|failed`, and with exactly one file also `result=<kit asset uuid>` or
 * `reel=<reel uuid>`: the Work page then marks that row (`workLinkTarget.ts`, design assets-rethink
 * f4). `state=` makes the link show the page also when the file finishes before the page opens.
 * A finished file has no such link: it opens in the Kit tab or in Posts, as before.
 * Pure, so the rules are tested without React.
 */
export interface WorkFile {
  kind: 'asset' | 'reel';
  uuid: string;
}

export interface WorkProgressParams {
  catalogItemUuid: string;
  state: 'working' | 'failed';
  result?: string;
  reel?: string;
}

const REEL_WORKING: ReadonlyArray<ReelStatus> = ['queued', 'analysing', 'planning', 'rendering'];

type LinkAsset = Pick<SerializedKitAsset, 'uuid' | 'themeId' | 'status' | 'supersededAt'>;
type LinkReel = Pick<SerializedReel, 'uuid' | 'status' | 'createdAt'>;

/**
 * The files of a song that are made now (`working`) or failed. `themeId` keeps one asset type (a
 * Studio card); the reels belong to the `aiReel` type. A failed reel counts only when it is the
 * newest reel: a newer try closes the failure (the rule of the Studio card).
 */
export function workFilesOf(input: {
  assets: ReadonlyArray<LinkAsset>;
  reels: ReadonlyArray<LinkReel>;
  state: 'working' | 'failed';
  themeId?: StudioThemeId;
}): WorkFile[] {
  const { assets, reels, state, themeId } = input;
  const files: WorkFile[] = assets
    .filter((asset) => asset.supersededAt === null && (themeId === undefined || asset.themeId === themeId))
    .filter((asset) =>
      state === 'working' ? KIT_ASSET_WORKING_STATUSES.includes(asset.status) : asset.status === 'failed'
    )
    .map((asset) => ({ kind: 'asset', uuid: asset.uuid }));
  if (themeId !== undefined && themeId !== 'aiReel') {
    return files;
  }
  const newestFirst = [...reels].sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  const reelFiles =
    state === 'working'
      ? newestFirst.filter((reel) => REEL_WORKING.includes(reel.status))
      : newestFirst.slice(0, 1).filter((reel) => reel.status === 'failed');
  return [...files, ...reelFiles.map((reel): WorkFile => ({ kind: 'reel', uuid: reel.uuid }))];
}

/** The parameters of the Work route for the work of a song. One file: its row gets the mark. */
export function workProgressParams(
  catalogItemUuid: string,
  state: 'working' | 'failed',
  files: ReadonlyArray<WorkFile>
): WorkProgressParams {
  const [only] = files;
  if (files.length !== 1 || only === undefined) {
    return { catalogItemUuid, state };
  }
  return only.kind === 'reel'
    ? { catalogItemUuid, state, reel: only.uuid }
    : { catalogItemUuid, state, result: only.uuid };
}

/** The same parameters as the query of a link (`SMLLink`): only the keys that have a value. */
export function workProgressQuery(
  catalogItemUuid: string,
  state: 'working' | 'failed',
  files: ReadonlyArray<WorkFile>
): Record<string, string> {
  const { result, reel } = workProgressParams(catalogItemUuid, state, files);
  return {
    catalogItemUuid,
    state,
    ...(result === undefined ? {} : { result }),
    ...(reel === undefined ? {} : { reel }),
  };
}
